import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Tooltip,
  Button,
  TextField,
} from "@mui/material";
import { TableSortLabel } from "@mui/material";
import {
  getComparator,
  stableSort,
} from "../../../../common-utilities/sortingUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import {
  GetAllCoolerProfiles,
  DeleteCoolerProfile,
} from "../../../services/CoolerProfileService";
import ViewCoolerProfile from "./ViewCoolerProfile";
import CommonLoader from "../../common/CommonLoader";
import { useNavigate } from "react-router-dom";

const CoolerProfilesTable = () => {
  const columns = [
    { id: "coolerProfileId", label: "ID" },
    { id: "profileName", label: "Profile Name" },
    { id: "coolerSize", label: "Size" },
    { id: "shippingWeight", label: "Weight" },
    { id: "dimensions", label: "W × H × D" },
    { id: "actions", label: "Actions" },
  ];

  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [profileData, setProfileData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [searchText, setSearchText] = useState("");
  const [tableHeight, setTableHeight] = useState(600);

  const tableContainerRef = useRef(null);
  const navigate = useNavigate();

  // Dynamic table height calculation
  useEffect(() => {
    const calculateTableHeight = () => {
      if (tableContainerRef.current) {
        const containerTop =
          tableContainerRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        const availableHeight = windowHeight - containerTop - 76;
        const calculatedHeight = Math.max(
          300,
          Math.min(availableHeight, windowHeight - 200)
        );
        setTableHeight(calculatedHeight);
      }
    };

    calculateTableHeight();
    window.addEventListener("resize", calculateTableHeight);
    const timeoutId = setTimeout(calculateTableHeight, 100);

    return () => {
      window.removeEventListener("resize", calculateTableHeight);
      clearTimeout(timeoutId);
    };
  }, [filteredData]);

  useEffect(() => {
    fetchProfiles();
  }, [isDeleted]);

  useEffect(() => {
    if (!searchText) {
      setFilteredData(profileData);
    } else {
      const q = searchText.toLowerCase();
      setFilteredData(
        profileData.filter(
          (r) =>
            (r.profileName || "").toLowerCase().includes(q) ||
            (r.coolerSize || "").toLowerCase().includes(q)
        )
      );
    }
  }, [searchText, profileData]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const response = await GetAllCoolerProfiles();
      const data = response?.results || [];
      setProfileData(data);
      setFilteredData(data);
    } catch (error) {
      console.error("Error fetching cooler profiles:", error);
      NotificationManager.error("Error fetching cooler profiles");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (profile) => setSelectedProfile(profile);

  const handleBackClick = () => setSelectedProfile(null);

  const handleDeleteClick = (profile) => {
    setSelectedProfile(profile);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedProfile(null);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      setLoading(true);
      const response = await DeleteCoolerProfile(selectedProfile.coolerProfileId);
      if (response.results === true) {
        NotificationManager.success("Cooler profile deleted successfully", "Success");
        setIsDeleted(!isDeleted);
      } else {
        NotificationManager.error("Error deleting cooler profile", "Error");
      }
    } catch (error) {
      NotificationManager.error("Error deleting cooler profile", "Error");
    } finally {
      setLoading(false);
      setSelectedProfile(null);
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleEditClick = (profile) =>
    navigate(`/cooler-profiles/${profile.coolerProfileId}`);

  const getDimensions = (profile) => {
    const w = profile.shippingWidth || "-";
    const h = profile.shippingHeight || "-";
    const d = profile.shippingDepth || "-";
    return `${w} × ${h} × ${d}`;
  };

  const getWeight = (profile) => {
    if (!profile.shippingWeight) return "N/A";
    return `${profile.shippingWeight} ${profile.shippingWeightUnit || ""}`.trim();
  };

  const renderCell = (col, profile) => {
    if (col.id === "shippingWeight") return getWeight(profile);
    if (col.id === "dimensions") return getDimensions(profile);
    return profile[col.id] || "N/A";
  };

  const renderActions = (profile) => (
    <div style={{ minWidth: "100px", display: "flex" }}>
      <Tooltip title="Edit Profile">
        <IconButton
          style={{ color: "orange" }}
          onClick={() => handleEditClick(profile)}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="View Profile">
        <IconButton
          style={{ color: "green" }}
          onClick={() => handleViewClick(profile)}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Delete Profile">
        <IconButton
          style={{ color: "red" }}
          onClick={() => handleDeleteClick(profile)}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <>
      <span className="title">Cooler Profiles</span>
      <hr />

      {selectedProfile ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewCoolerProfile profile={selectedProfile} />
        </>
      ) : (
        <>
          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
            }}
          >

            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: 1, maxWidth: "400px" }}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/cooler-profiles/add")}
            >
              Add Profile
            </Button>
          </div>

          <Paper>
            <TableContainer
              ref={tableContainerRef}
              sx={{ maxHeight: tableHeight, overflowY: "auto", width: "99%" }}
            >
              <Table size="small" stickyHeader>
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.id}>
                        {col.id === "actions" || col.id === "dimensions" ? (
                          col.label
                        ) : (
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleRequestSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {stableSort(filteredData, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((profile, index) => (
                      <TableRow
                        key={profile.coolerProfileId}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0,0,0,0.04)" : "white",
                        }}
                      >
                        {columns.map((col) => (
                          <TableCell key={col.id}>
                            {col.id === "actions"
                              ? renderActions(profile)
                              : renderCell(col, profile)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      )}

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        message="Are you sure you want to delete this cooler profile?"
      />

      <CommonLoader loading={loading} />
    </>
  );
};

export default CoolerProfilesTable;